import React, { FC, useCallback, useState } from "react";
import clsx from "clsx";
import Divider from "@material-ui/core/Divider";
import makeStyles from "@material-ui/core/styles/makeStyles";
import { ApplicationStatusControl } from "./ApplicationStatusControl/ApplicationStatusControl";
import { ActionButton } from "./ActionButtons";
import { ApplicationStatusInfo } from "./ApplicationStatusInfo";
import { useApplicationConfig } from "../../modals/hooks/useApplicationConfig";
import { EducationType } from "../../../school/constants/types";
import { useWizard } from "../../../wizard/context";
import { useAcademicsPageHigher } from "../../../academic/hooks/useAcademicsPageHigher";
import { useAcademicsPageSecondary } from "../../../academic/hooks/useAcademicsPageSecondary";
import WeduTooltip from "../../../../components/WeduTooltip/intex";
import FontIcon from "../../../../components/FontIcon";
import { Note } from "./Note";
import { getApplicationCancellationInfo } from "../../helpers/getApplicationCancellationInfo";
import { CardField } from "../../../../components/CardField/CardField";
import { ISchoolStudentApplicationCardProps } from "../../types";
import { ApplicationCardHeader } from "./ApplicationCardHeader";
import { useLanguageCourseConfig } from "../../../language-course/modals/hooks/useLanguageCourseConfig";

const useStyles = makeStyles((theme) => ({
  card: {
    width: 732,
    background: "#FFFFFF",
    boxShadow: "0px 0px 8px #C2C9D682",
    borderRadius: 15,
    "& + &": {
      marginTop: 20,
    },
  },
  header: {
    borderBottom: "1px solid #DBDBDB;",
    padding: 15,
  },
  centerContent: {
    height: 109,
    borderBottom: "1px solid #DBDBDB;",
    padding: "5px 5px",
    display: "grid",
    columnGap: 2,
    gridTemplateColumns: "1fr 1fr 1fr",
  },
  footer: {
    height: 50,
    padding: "15px 15px",
    display: "flex",
    justifyContent: "space-between",
    alignItems: "center",
  },
  disabled: {
    opacity: 0.5,
    background: "#f8f9fb",
    pointerEvents: "none",
  },
  icon: {
    width: 32,
    height: 32,
  },
  statusAndNoteContainer: {
    display: "flex",
    alignItems: "center",
  },
  divider: {
    alignSelf: "auto",
    backgroundColor: "#DBDBDB",
    height: "24px",
    margin: "0px 8px",
    width: "1px",
  },
  weduTooltip: {
    height: 16,
    marginLeft: 5,
  },
  warningIcon: {
    color: theme.palette.error.main,
  },
}));

export const SchoolStudentApplicationCard: FC<
  ISchoolStudentApplicationCardProps
> = ({ application, schoolType }) => {
  const classes = useStyles();

  const { openWizard } = useWizard();
  const [expandedCardFieldId, setExpandedCardFieldId] = useState(null);
  const { getViewApplicationConfig } = useApplicationConfig();
  const { programWizardConfigs: programWizardConfigsHigher } =
    useAcademicsPageHigher(true);
  const { programWizardConfigs: programWizardConfigsSecondary } =
    useAcademicsPageSecondary(true);
  const { getViewConfig } = useLanguageCourseConfig();
  const expandHandler = useCallback((id) => {
    setExpandedCardFieldId((prev) => {
      return prev === id ? null : id;
    });
  }, []);

  const closeCard = () => setExpandedCardFieldId(null);

  const applyViewApplication = async (id, educationType) => {
    await openWizard(getViewApplicationConfig(id, educationType));
  };

  const viewEntity = async (id, educationType) => {
    if (educationType === EducationType.Higher) {
      await openWizard(programWizardConfigsHigher.getViewConfig(id));
    } else if (educationType === EducationType.Secondary) {
      await openWizard(programWizardConfigsSecondary.getViewConfig(id));
    } else if (educationType === EducationType.Language) {
      await openWizard(getViewConfig(id));
    }
  };

  const { note, isDisabled, warningIconTooltipText } =
    getApplicationCancellationInfo(application);

  return (
    <div className={classes.card}>
      <div className={clsx(classes.header, isDisabled && classes.disabled)}>
        <ApplicationCardHeader content={application.headerContent} />
      </div>
      <div
        className={clsx(classes.centerContent, isDisabled && classes.disabled)}
      >
        {application.centerContent.map((cardField) => (
          <CardField
            key={cardField.id}
            id={cardField.id}
            Icon={cardField.icon}
            title={cardField.title}
            PreviewContent={cardField.previewContent}
            ExpandedContent={cardField.expandedContent}
            onExpand={expandHandler}
            onClickAway={closeCard}
            isExpanded={expandedCardFieldId === cardField.id}
          />
        ))}
      </div>
      <div className={classes.footer}>
        <div className={classes.statusAndNoteContainer}>
          {isDisabled ? (
            <ApplicationStatusInfo status={application.status} />
          ) : (
            <ApplicationStatusControl
              status={application.status.type}
              id={application.id}
              schoolType={schoolType}
            />
          )}
          {warningIconTooltipText && (
            <WeduTooltip
              title={warningIconTooltipText}
              className={classes.weduTooltip}
            >
              <FontIcon
                className={classes.warningIcon}
                name={"warning"}
                fontSize={"small"}
              />
            </WeduTooltip>
          )}
          {note && (
            <>
              <Divider className={classes.divider} orientation={"vertical"} />
              <Note note={note} />
            </>
          )}
        </div>
        <ActionButton
          viewApp={() => applyViewApplication(application.id, schoolType)}
          viewTooltip={"View application"}
          redirect={() => viewEntity(application.entityId, schoolType)}
          redirectTooltip={`View ${application.entityName}`}
        />
      </div>
    </div>
  );
};
